/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

// Speech to Text Plan Mapping
const speechToTextPlanNames = ['Lite', 'Standard', 'Plus', 'Premium'];
const speechToTextPlanIds = [
  '61a9c4f2-48ec-400c-9881-1ffde3bfc5be',
  'a0bcad8e-13a4-421f-9741-377f983406e124',
  '70680f1c-5493-4947-93ef-ba6076fdb4d1',
  '8072d889-f174-4055-bbf6-8fea85db94e0'
];

// Text to Speech Plan Mapping
const textToSpeechPlanNames = ['Lite', 'Standard', 'Premium'];
const textToSpeechPlanIds = [
  '2580b607-db64-4883-9793-445b694ed57b',
  '280fcf67-0dcd-4aed-ba69-d25893c947121',
  '66b200c3-0b3f-4699-a39d-b0576d28d6f8'
];

const getPlanIdByName = (names, ids) => planName => {
  const index = names.findIndex(name => name === planName);
  const id = ids[index]; // Will return undefined if no match.
  return id;
};

const getPlanNameById = (names, ids) => planGuid => {
  const index = ids.findIndex(id => id === planGuid);
  const name = names[index]; // Will return undefined if no match.
  return name;
};

const getSttPlanNameById = getPlanNameById(
  speechToTextPlanNames,
  speechToTextPlanIds
);

const getSttPlanIdByName = getPlanIdByName(
  speechToTextPlanNames,
  speechToTextPlanIds
);

const getTtsPlanNameById = getPlanNameById(
  textToSpeechPlanNames,
  textToSpeechPlanIds
);

const getTtsPlanIdByName = getPlanIdByName(
  textToSpeechPlanNames,
  textToSpeechPlanIds
);

const getSpeechPlanNameById = id => {
  return getSttPlanNameById(id) || getTtsPlanNameById(id);
};

// Plan IDs for Premium Speech -- Recommended
// if in Enterprise Data Isolation Plan.
const isPremiumDataIsolPlan = id => {
  return (
    getSttPlanNameById(id) === 'Premium' || getTtsPlanNameById(id) === 'Premium'
  );
};

const isPremiumSpeech = instance => {
  return instance?.plan?.guid
    ? isPremiumDataIsolPlan(instance.plan.guid)
    : false;
};

const hasPremiumSpeech = instances => {
  return instances.filter(instance => isPremiumSpeech(instance)).length > 0;
};

/**
 * Given a nested object, return a flattened version
 * example: {a: {A: {B: 'first'}, C: { D: 'second'}}}}
 *         --> {a__A__B: 'first',
 *              a__A__C__D: 'second'}
 *
 * Each layer is split by '__'
 *
 * @param {*} obj Object to flatten
 * @param {*} prefix Optional object to start prefix
 */

const flattenObject = (obj, prefix) => {
  // If not an object, return it flat
  if (!obj) {
    return { [prefix]: obj };
  }
  if (typeof obj !== 'object') {
    return { [prefix]: obj };
  }
  if (Array.isArray(obj)) {
    return { [prefix]: obj };
  }
  const finalPrefix = prefix ? `${prefix}__` : '';
  return Object.keys(obj).reduce((current, key) => {
    current = {
      ...current,
      ...flattenObject(obj[key], `${finalPrefix}${key}`)
    };
    return current;
  }, {});
};

export {
  flattenObject,
  getSpeechPlanNameById,
  getSttPlanIdByName,
  getSttPlanNameById,
  getTtsPlanIdByName,
  getTtsPlanNameById,
  isPremiumDataIsolPlan,
  hasPremiumSpeech,
  isPremiumSpeech
};
